import {
    DetailedHTMLProps,
    forwardRef,
    InputHTMLAttributes,
    useId,
} from 'react';
import classNames from 'classnames';

import styles from './Input.module.scss';

type HTMLInputProps = DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
>;

export interface InputProps extends Omit<HTMLInputProps, 'size'> {
    label?: string;
    color?:
        | 'default'
        | 'primary'
        | 'secondary'
        | 'positive'
        | 'danger'
        | 'warning';
    isInvalid?: boolean;
    errorMessage?: string;
    size?: 'large' | 'small' | 'medium';
    inputSize?: HTMLInputProps['size'];
    inputClassName?: string;
}

/**
 * Styled input component with additional functionality, such as built-in label
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
    {
        label,
        isInvalid,
        errorMessage,
        color,
        className,
        inputClassName,
        inputSize,
        size = 'medium',
        ...inputProps
    },
    ref,
) {
    const inputId = useId();

    return (
        <>
            <div
                className={classNames(
                    className,
                    styles.container,
                    color && styles[color],
                    {
                        [styles.sizeSmall]: size === 'small',
                        [styles.sizeLarge]: size === 'large',
                        [styles.sizeMedium]: size === 'medium',
                        [styles.noLabel]: !label,
                    },
                )}
            >
                <input
                    id={inputId}
                    className={classNames(styles.input, inputClassName)}
                    ref={ref}
                    size={inputSize}
                    {...inputProps}
                />
                {label && (
                    <label htmlFor={inputId} className={styles.label}>
                        {label}
                    </label>
                )}
            </div>
            {isInvalid && errorMessage && (
                <p
                    className={classNames(
                        styles.validationText,
                        color && styles[color],
                    )}
                >
                    {errorMessage}
                </p>
            )}
        </>
    );
});
